import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TableUrlInput } from '@/components/ui-v2/inputs/table/table-url-input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { cn } from '@/lib/utils';
import { Row } from '@tanstack/react-table';
import { InfoIcon } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleCellClick: (id: string, field: keyof DealItem) => void;
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;
  handleKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
}

interface UrlCellContentProps {
  row: Row<DealItemWithVersions>;
  handleCellClick: ColumnProps['handleCellClick'];
  handleCellEdit: ColumnProps['handleCellEdit'];
  handleKeyDown: ColumnProps['handleKeyDown'];
  handleBlur: ColumnProps['handleBlur'];
}

function UrlCellContent({
  row,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: UrlCellContentProps) {
  const isOfferLocked = useIsOfferLocked();
  const isEditing = useIsCellEditing(row, 'sourceUrl');
  const isParent = row.subRows.length > 1;

  const getValue = () => row.original.activeVersion.sourceUrl;

  const getParentValue = () => {
    const subRowsValues: (string | null)[] = [];
    row.subRows.forEach((subRow) => {
      const value = subRow.original.activeVersion.sourceUrl;
      if (typeof value !== 'undefined' && value !== null) {
        subRowsValues.push(value);
      }
    });
    if (subRowsValues.length === 0) return null;
    const firstValue = subRowsValues[0];
    const allSame = subRowsValues.every((val) => val === firstValue);
    return allSame ? firstValue : null;
  };

  const displayValue = isParent ? getParentValue() : getValue();

  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="sourceUrl"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <div
          className={cn(
            'h-full w-full',
            (isParent || isOfferLocked) && 'opacity-50',
            isParent || !isOfferLocked ? 'cursor-pointer' : 'cursor-not-allowed'
          )}
          onClick={() =>
            !isParent &&
            handleCellClick(row.original.activeVersion.id, 'sourceUrl')
          }
        >
          <TableUrlInput
            value={row.original.activeVersion.sourceUrl ?? null}
            onChange={async (value) => {
              await handleCellEdit(
                row.original.activeVersion.id,
                'sourceUrl',
                value,
                !isParent
              );
            }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={isOfferLocked}
            autoFocus={isEditing}
            displayValue={displayValue ?? null}
            isEditing={isEditing}
            isRecentlyModified={isRecentlyModified}
          />
        </div>
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefURL = ({
  handleBlur,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();

  const accessorKey: keyof DealItem = 'sourceUrl';
  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'URL',
      header: () => (
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <InfoIcon className="h-4 w-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('kpi.url_tooltip')}</p>
            </TooltipContent>
          </Tooltip>
          {t('kpi.url')}
        </div>
      ),
      cell: ({ row }) => (
        <UrlCellContent
          row={row}
          handleCellClick={handleCellClick}
          handleCellEdit={handleCellEdit}
          handleKeyDown={handleKeyDown}
          handleBlur={handleBlur}
        />
      ),
      size: 300,
    }),
    [t, handleBlur, handleCellClick, handleCellEdit, handleKeyDown]
  );
};

export default useColumnDefURL;
